// Search: the two questions the search bar asks the server.
//
//   find   the named things of one project that hold the typed text, in groups
//   thing  one of those things again, by the id its row carried
//
// Both are quiet (`skipSessionExpiry`), for the reason the home screen's counts are
// (see homeCountsAPI in ./api): the app asks while the person types, and these routes
// answer 401 and 403 to someone who is signed in whenever they cannot name the caller.
// Asked plainly, that would sign a person out for typing in a box. So every non-2xx
// means one thing to the caller: this could not be searched.
//
// What is sent is spelled out here and nowhere else: the project and the text, or the
// project, the kind and the id. Neither call is wrapped in product-load tracking, and
// nothing here logs: what a person types into search is recorded nowhere by the web.
import api from './api';

/** The kinds, in the order the server answers their groups. */
export const THING_KINDS = ['person', 'store', 'task', 'training', 'product', 'dashboard', 'report'] as const;
export type ThingKind = (typeof THING_KINDS)[number];

/** One result line. `id` is what `thing` takes back; a store's or product's own code is in `code`. */
export interface ThingRow {
  id: string;
  name: string;
  detail: string;
  code?: string;
}

/** A 'failed' group has no rows and is not "nothing found"; `more` says the list was cut short. */
export interface ThingGroup {
  kind: ThingKind;
  status: 'ok' | 'failed';
  rows: ThingRow[];
  more: boolean;
}

export interface Thing extends ThingRow {
  kind: ThingKind;
}

type Envelope<T> = { success: boolean; data: T };
type QuietConfig = NonNullable<Parameters<typeof api.get>[1]> & { skipSessionExpiry: true };
const quiet = (params: Record<string, unknown>, signal?: AbortSignal): QuietConfig => ({ params, signal, skipSessionExpiry: true });

export const searchAPI = {
  find: (p: { projectId: number; q: string }, signal?: AbortSignal) =>
    api.get<Envelope<{ q: string; groups: ThingGroup[] }>>('/search', quiet({ projectId: p.projectId, q: p.q }, signal)),
  thing: (p: { projectId: number; kind: ThingKind; id: string }, signal?: AbortSignal) =>
    api.get<Envelope<Thing>>('/search/thing', quiet({ projectId: p.projectId, kind: p.kind, id: p.id }, signal)),
};
